import { isBotUserAgent } from '@/shared/lib/isBotUserAgent'

import {
  LOCK_AB_ALLOCATION,
  LOCK_AB_CLICK_TRACK_TIMEOUT_MS,
  LOCK_AB_COOKIE_MAX_AGE_SEC,
  LOCK_AB_COOKIE_NAME,
  LOCK_AB_COOKIE_PATH,
  LOCK_AB_EXPERIMENT_ENABLED,
  LOCK_AB_VARIANT,
} from '../_constants'

import type { TLockAbAfSub2, TLockAbFrom, TLockAbVariant } from '../_constants'

export interface ILockAbAssignment {
  variant: TLockAbVariant
  from: TLockAbFrom | null
  writeCookie: boolean
  afSub1: string | null
}

export const LOCK_AB_EXCLUDED: ILockAbAssignment = {
  variant: LOCK_AB_VARIANT.DEFAULT,
  from: null,
  writeCookie: false,
  afSub1: null,
}

const EXTRA_CRAWLER_UA_REGEX = /daum|chatgpt-user|claude-user|google-extended/i

/** [정책]: 크롤러는 SEO 색인 화면을 고정하려고 항상 A를 봅니다. */
export const isLockAbExcludedCrawler = (userAgent: string | null): boolean =>
  Boolean(userAgent) &&
  (isBotUserAgent(userAgent!) || EXTRA_CRAWLER_UA_REGEX.test(userAgent!))

export const isLockAbVariant = (
  value: string | null | undefined
): value is TLockAbVariant =>
  value === LOCK_AB_VARIANT.DEFAULT || value === LOCK_AB_VARIANT.LOCK

const isExperimentActive = (): boolean =>
  LOCK_AB_EXPERIMENT_ENABLED && LOCK_AB_ALLOCATION > 0

export const getLockAbRandomBucket = (): number =>
  globalThis.crypto.getRandomValues(new Uint32Array(1))[0] % 100

interface IResolveLockAbParams {
  cookieValue: string | null | undefined
  overrideValue: string | null | undefined
  isInApp: boolean
  userAgent: string | null
  getBucket?: () => number
}

/**
 * [주의]: 판정 순서는 인앱·크롤러 → 강제 분기 → 킬 스위치·ALLOCATION 0 → 기존 쿠키 → 신규 배정입니다.
 * 순서를 바꾸면 인앱 웹뷰나 크롤러도 B에 배정될 수 있습니다.
 */
export const resolveDetailLockAb = ({
  cookieValue,
  overrideValue,
  isInApp,
  userAgent,
  getBucket = getLockAbRandomBucket,
}: IResolveLockAbParams): ILockAbAssignment => {
  if (isInApp || isLockAbExcludedCrawler(userAgent)) return LOCK_AB_EXCLUDED

  if (isLockAbVariant(overrideValue)) {
    return {
      variant: overrideValue,
      from: 'override',
      writeCookie: false,
      afSub1: `qa_${overrideValue}`,
    }
  }

  if (!isExperimentActive()) {
    return {
      variant: LOCK_AB_VARIANT.DEFAULT,
      from: 'disabled',
      writeCookie: false,
      afSub1: null,
    }
  }

  if (isLockAbVariant(cookieValue)) {
    return {
      variant: cookieValue,
      from: 'returning',
      writeCookie: true,
      afSub1: cookieValue,
    }
  }

  const variant =
    getBucket() < LOCK_AB_ALLOCATION
      ? LOCK_AB_VARIANT.LOCK
      : LOCK_AB_VARIANT.DEFAULT
  return { variant, from: 'new', writeCookie: true, afSub1: variant }
}

/** [정책]: 랭킹 페이지는 화면을 바꾸지 않고 배정도 하지 않습니다. 쿠키가 있을 때만 만료를 연장하고 노출 이벤트와 af_sub1을 남깁니다. */
export const resolveRankLockAb = ({
  cookieValue,
  overrideValue,
  isInApp,
  userAgent,
}: Omit<IResolveLockAbParams, 'getBucket'>): ILockAbAssignment => {
  if (isInApp || isLockAbExcludedCrawler(userAgent)) return LOCK_AB_EXCLUDED

  if (isLockAbVariant(overrideValue)) {
    return {
      variant: overrideValue,
      from: 'override',
      writeCookie: false,
      afSub1: `qa_${overrideValue}`,
    }
  }

  if (!isExperimentActive()) {
    return {
      variant: LOCK_AB_VARIANT.DEFAULT,
      from: 'disabled',
      writeCookie: false,
      afSub1: null,
    }
  }

  if (!isLockAbVariant(cookieValue)) return LOCK_AB_EXCLUDED

  return {
    variant: cookieValue,
    from: 'returning',
    writeCookie: true,
    afSub1: cookieValue,
  }
}

export const buildLockAbCookie = (variant: TLockAbVariant): string =>
  [
    `${LOCK_AB_COOKIE_NAME}=${variant}`,
    `Path=${LOCK_AB_COOKIE_PATH}`,
    `Max-Age=${LOCK_AB_COOKIE_MAX_AGE_SEC}`,
    'SameSite=Lax',
    'Secure',
  ].join('; ')

export const readLockAbCookie = (cookieString: string): string | null => {
  const prefix = `${LOCK_AB_COOKIE_NAME}=`
  const entry = cookieString
    .split(';')
    .map(part => part.trim())
    .find(part => part.startsWith(prefix))
  return entry ? entry.slice(prefix.length) : null
}

export const withLockAbAfSubs = (
  onelinkUrl: string,
  { afSub1, afSub2 }: { afSub1: string | null; afSub2: TLockAbAfSub2 }
): string => {
  try {
    const url = new URL(onelinkUrl)
    if (afSub1) url.searchParams.set('af_sub1', afSub1)
    url.searchParams.set('af_sub2', afSub2)
    return url.toString()
  } catch {
    return onelinkUrl
  }
}

export const trackThenNavigate = async (
  track: () => Promise<unknown> | void,
  navigate: () => void
): Promise<void> => {
  const timeout = new Promise<void>(resolve =>
    setTimeout(resolve, LOCK_AB_CLICK_TRACK_TIMEOUT_MS)
  )
  try {
    await Promise.race([Promise.resolve(track()), timeout])
  } catch {
    // 이벤트 실패와 무관하게 이동은 진행한다
  }
  navigate()
}
